import axios from 'axios';

/**
 * Configure axios for secure communication with backend.
 *
 * Features:
 * - Configurable backend URL via REACT_APP_API_BASE_URL env var
 * - Enables httpOnly cookie support (credentials)
 * - Fetches and includes CSRF tokens in requests
 * - Handles token refresh on 401 responses
 */

// Set base URL from environment variable (defaults to current origin for development)
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
axios.defaults.baseURL = apiBaseUrl;

// Enable credentials (httpOnly cookies) for all requests
axios.defaults.withCredentials = true;

// Store CSRF token
let csrfToken = '';

/**
 * Fetch CSRF token from backend.
 * Should be called once on app initialization.
 * 
 * Returns:
 *   Promise: Resolves when CSRF token is fetched
 */
export const initializeCSRFToken = async () => {
  try {
    const response = await axios.get('/api/csrf-token');
    csrfToken = response.data.csrfToken;
    axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
    return csrfToken;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
};

/**
 * Get current CSRF token.
 * 
 * Returns:
 *   string: Current CSRF token
 */
export const getCSRFToken = () => csrfToken;

/**
 * Create axios instance with security headers.
 * 
 * Returns:
 *   AxiosInstance: Configured axios instance
 */
export const createSecureAxios = () => {
  const instance = axios.create({
    withCredentials: true,
    headers: {
      'X-CSRF-Token': csrfToken
    }
  });

  // Add response interceptor to handle 401 (token expired)
  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        // Token expired or invalid
        // Clear any stored data and redirect to login
        window.location.href = '/';
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export default axios;

